import React, { useEffect, useReducer } from 'react';

import Amplify from 'aws-amplify';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import awsConfig from 'aws-config';
import { SIGNIN } from 'redux/sagas/actions/authentication';
import { SET_ACCESS_ERROR, SET_FORGOT_STEP } from 'redux/slices/authentication';

import { Button, Input, ToggleSwitch, Tooltip } from 'ui/atoms';
import MainFont from 'ui/typography/mainFont';
import { getCookie, setCookie } from 'utils/cookies';

import routes from '../../routing/routes';

import {
  ForgotSection,
  ForgotText,
  InputsSection,
  LoginWelcomeText,
  RememberMeSection,
  RowCentered,
  RowForm,
  SubmitWrapper
} from './style';
import ReactTooltip from 'react-tooltip';

const reducer = (state, action) => {
  switch (action.type) {
    case 'add_username':
      return {
        ...state,
        username: action.payload
      };
    case 'add_password':
      return {
        ...state,
        password: action.payload
      };

    default:
      return state;
  }
};

const newField = () => '';

const initialState = {
  username: newField(),
  password: newField()
};

const Login = ({
  signIn,
  error,
  redirectToCreatePassword,
  resetError,
  userStored,
  setForgotStep,
  config
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  initialState.username = userStored;

  const { t } = useTranslation();
  const history = useHistory();

  const handleLogin = () => {
    const { username, password } = state;
    const user = username.trim();
    signIn({ user, password });
  };

  const handleToggle = async value => {
    setCookie('loginStore', value ? 'localStorage' : 'sessionStorage');
    const conf = await awsConfig(config);
    Amplify.configure(conf);
  };

  const onClickforgotPassword = () => {
    setForgotStep();
    history.push(routes.forgotpassword.path);
  };

  const loginStore = getCookie('loginStore');

  useEffect(() => {
    if (redirectToCreatePassword) {
      history.push(routes.createNewPassword.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToCreatePassword]);

  const isDisabled = () => !state.username || !state.password;

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  useEffect(() => {
    resetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refTooltip = React.useRef();

  useEffect(() => {
    if (error) {
      ReactTooltip.show(refTooltip.current);

      setTimeout(() => {
        ReactTooltip.hide(refTooltip.current);
      }, 2000);
    }
  }, [error]);

  return (
    <>
      <InputsSection onKeyPress={handleKeyPress}>
        <RowCentered>
          <LoginWelcomeText
            data-tip={t('incorrect_login')}
            data-for="login-error"
            data-offset="{'top': -50}"
            ref={refTooltip}
          >
            <MainFont type="4">{t('login_welcome_text')}</MainFont>
          </LoginWelcomeText>
        </RowCentered>
        <RowForm>
          <Input
            placeholder={t('email_placeholder')}
            variant="login"
            type="text"
            value={state.username}
            maxLength="128"
            onChange={e =>
              dispatch({
                type: 'add_username',
                payload: e.currentTarget.value
              })
            }
            status={error ? 'invalid' : ''}
          />
          {error && <Tooltip id="login-error" error />}
        </RowForm>
        <RowForm>
          <Input
            placeholder={t('password')}
            type="password"
            variant="login"
            value={state.password}
            onChange={e =>
              dispatch({
                type: 'add_password',
                payload: e.currentTarget.value
              })
            }
            isShowPassword
            status={error ? 'invalid' : ''}
          />
        </RowForm>
        <ForgotSection>
          <ForgotText onClick={onClickforgotPassword}>
            <MainFont type="3">{`${t('forgot_password')}?`}</MainFont>
          </ForgotText>
        </ForgotSection>
        <RememberMeSection>
          <ToggleSwitch
            text={t('keep_me')}
            onChange={handleToggle}
            checked={loginStore && loginStore === 'localStorage'}
          />
        </RememberMeSection>
      </InputsSection>

      <RowCentered>
        <SubmitWrapper>
          <Button
            variant="login"
            disabled={isDisabled()}
            type="button"
            onClick={isDisabled() ? () => null : handleLogin}
          >
            {t('login')}
          </Button>
        </SubmitWrapper>
      </RowCentered>
    </>
  );
};

Login.propTypes = {
  signIn: PropTypes.func,
  error: PropTypes.string,
  userStored: PropTypes.string,
  redirectToCreatePassword: PropTypes.bool,
  resetError: PropTypes.func,
  setForgotStep: PropTypes.func,
  config: PropTypes.object
};

export default connect(
  state => ({
    userStored: state?.app.user,
    error: state?.authentication.errorMessage,
    redirectToCreatePassword: state?.authentication.redirectToCreatePassword,
    config: state.app?.config
  }),
  dispatch => ({
    signIn: payload => dispatch({ type: SIGNIN._REQUEST, payload }),
    resetError: () => dispatch(SET_ACCESS_ERROR(null)),
    setForgotStep: () => dispatch(SET_FORGOT_STEP())
  })
)(Login);
